/**
 * /decks/new — create a new deck on a chosen project, starting from the
 * canonical-pitch template. Once the API responds with the new deck row,
 * we redirect to /decks/:id.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateDeck, useUserProjects } from "../hooks";
import { CANONICAL_THEME, buildCanonicalPitchDeckJson } from "../canonicalTemplate";

const DEFAULT_SLUG = "untitled-deck";

export function NewDeckPage() {
  const navigate = useNavigate();
  const projects = useUserProjects();
  const [projectId, setProjectId] = useState<string>("");
  const [title, setTitle] = useState("Untitled Deck");
  const [slug, setSlug] = useState(DEFAULT_SLUG);
  const [useTemplate, setUseTemplate] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-select the first project once they load — most users belong to one.
  useEffect(() => {
    if (projectId) return;
    const first = projects.data?.[0];
    if (first) {
      setProjectId(first.id);
    }
  }, [projectId, projects.data]);

  // Slug autogenerated from title until the user explicitly edits it.
  const [slugTouched, setSlugTouched] = useState(false);
  useEffect(() => {
    if (slugTouched) return;
    setSlug(slugify(title) || DEFAULT_SLUG);
  }, [title, slugTouched]);

  const createMutation = useCreateDeck(projectId, {
    onSuccess: (deck) => {
      navigate(`/decks/${deck.id}`);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const canSubmit =
    projectId.length > 0 &&
    title.trim().length > 0 &&
    /^[a-z0-9][a-z0-9-]*$/.test(slug) &&
    !createMutation.isPending;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!canSubmit) return;
    createMutation.mutate({
      slug,
      title: title.trim(),
      theme: CANONICAL_THEME,
      deck_json: useTemplate ? buildCanonicalPitchDeckJson(title.trim()) : undefined,
    });
  };

  const slugValid = useMemo(() => /^[a-z0-9][a-z0-9-]*$/.test(slug), [slug]);

  return (
    <div
      style={{
        maxWidth: 480,
        margin: "5vh auto",
        padding: "0 1.5rem",
        color: "var(--gray-12)",
      }}
      data-testid="new-deck-page"
    >
      <h1 style={{ marginBottom: "0.25rem" }}>New deck</h1>
      <p style={{ color: "var(--gray-9)", marginTop: 0 }}>
        Starts from the canonical Thread pitch deck unless you opt out.
      </p>

      <form onSubmit={submit} style={formStyle}>
        <label style={labelStyle}>
          Project
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={inputStyle}
            disabled={projects.isLoading}
            data-testid="new-deck-project-select"
          >
            <option value="">— pick a project —</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            data-testid="new-deck-title-input"
          />
        </label>

        <label style={labelStyle}>
          Slug
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            style={{
              ...inputStyle,
              borderColor: slugValid ? "var(--gray-7)" : "var(--red-9)",
            }}
            data-testid="new-deck-slug-input"
          />
          <span style={helpTextStyle}>
            Lowercase letters, digits, and hyphens. Must start with a letter or
            digit.
          </span>
        </label>

        <label
          style={{ ...labelStyle, flexDirection: "row", alignItems: "center" }}
        >
          <input
            type="checkbox"
            checked={useTemplate}
            onChange={(e) => setUseTemplate(e.target.checked)}
            style={{ marginRight: "0.5rem" }}
            data-testid="new-deck-template-toggle"
          />
          <span>Start from the canonical pitch deck</span>
        </label>

        {error && (
          <div style={errorStyle} data-testid="new-deck-error">
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              ...primaryBtnStyle,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            data-testid="new-deck-submit"
          >
            {createMutation.isPending ? "Creating…" : "Create deck"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/decks")}
            style={ghostBtnStyle}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  marginTop: "1.5rem",
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.9em",
  color: "var(--gray-11)",
};

const inputStyle: React.CSSProperties = {
  marginTop: "0.25rem",
  padding: "0.5rem 0.65rem",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  fontSize: "0.95em",
};

const helpTextStyle: React.CSSProperties = {
  marginTop: "0.25rem",
  fontSize: "0.8em",
  color: "var(--gray-9)",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--accent-9)",
  color: "white",
  border: "none",
  padding: "0.5rem 1rem",
  borderRadius: 4,
  fontSize: "0.95em",
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--gray-11)",
  border: "1px solid var(--gray-7)",
  padding: "0.5rem 1rem",
  borderRadius: 4,
  fontSize: "0.95em",
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  background: "var(--red-3)",
  color: "var(--red-12)",
  border: "1px solid var(--red-7)",
  borderRadius: 4,
  fontSize: "0.9em",
};
